import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { RefereeDto, RefereeLoginRequest } from '../services/refereeService';
import { universalAuthService } from '../services/universalAuthService';
import { useTokenRefresh } from '../hooks/useTokenRefresh';

interface RefereeContextType {
  referee: RefereeDto | null;
  loading: boolean;
  error: string | null;
  initializing: boolean;
  isRefereeAuthenticated: boolean;
  loginReferee: (email: string, password: string) => Promise<void>;
  logoutReferee: () => void;
  refreshRefereeData: () => Promise<void>;
}

const RefereeContext = createContext<RefereeContextType | undefined>(undefined);

// Функции для работы с данными судьи через универсальный сервис
const saveRefereeData = (referee: RefereeDto, token: string, refreshToken?: string) => {
  universalAuthService.saveData('referee', referee, token, refreshToken);
};

const clearRefereeData = () => {
  universalAuthService.clearData('referee');
};

export const RefereeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [referee, setReferee] = useState<RefereeDto | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [initializing, setInitializing] = useState<boolean>(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Инициализируем автоматическое обновление токенов для referee
  useTokenRefresh('referee');

  // Инициализация при старте приложения (только один раз)
  useEffect(() => {
    let isMounted = true;

    const initializeRefereeAuth = async () => {
      try {
        console.log('Initializing referee auth...');
        // Проверяем был ли судья залогинен до инициализации
        const hadStoredData = !!localStorage.getItem('refereeData');
        const refereeData = await universalAuthService.initializeAuth('referee');

        if (!isMounted) return;

        if (refereeData) {
          console.log('=== REFEREE CONTEXT INIT DEBUG ===');
          console.log('Loaded refereeData:', refereeData);
          console.log('isMainReferee:', refereeData.isMainReferee);
          console.log('fieldAssignments:', refereeData.fieldAssignments);
          console.log('==================================');

          setReferee(refereeData);
          console.log('Referee set successfully');

          // Перенаправление на профиль судьи если находимся на странице входа
          if (location.pathname === '/login') {
            navigate('/referee/profile');
          }
        } else {
          console.log('No valid referee data found');
          // Если были данные но токен истёк - редирект на главную
          if (hadStoredData && location.pathname !== '/') {
            console.log('Referee token expired, redirecting to homepage');
            navigate('/');
          }
        }
      } catch (error) {
        console.error('Error initializing referee auth:', error);
        clearRefereeData();
      } finally {
        if (isMounted) {
          setInitializing(false);
        }
      }
    };

    initializeRefereeAuth();

    return () => {
      isMounted = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Только при монтировании

  const logoutReferee = useCallback(() => {
    setReferee(null);
    setError(null);
    clearRefereeData();
    navigate('/');
  }, [navigate]);

  const refreshRefereeData = useCallback(async () => {
    if (!referee?.id) {
      return;
    }

    try {
      // В будущем здесь можно добавить получение обновленных данных судьи через API
      console.log('Refreshing referee data...');
    } catch (error: any) {
      console.error('Error refreshing referee data:', error);
      // Silent failure for refresh, как в AuthContext
    }
  }, [referee]);

  const loginReferee = useCallback(async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const refereeService = await import('../services/refereeService');
      
      const response = await refereeService.default.loginReferee({ email, password });

      // Сохраняем данные авторизации с refreshToken
      saveRefereeData(response.referee, response.token, response.refreshToken);

      // Устанавливаем судью
      setReferee(response.referee);
      
      // Перенаправление на профиль судьи после успешного входа
      navigate('/referee/profile');
    } catch (error: any) {
      const errorMessage = error.message || 'Login failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const isRefereeAuthenticated = !!referee;

  const value: RefereeContextType = {
    referee,
    loading,
    error,
    initializing,
    isRefereeAuthenticated,
    loginReferee,
    logoutReferee,
    refreshRefereeData,
  };

  return (
    <RefereeContext.Provider value={value}>
      {children}
    </RefereeContext.Provider>
  );
};

export const useRefereeAuth = (): RefereeContextType => {
  const context = useContext(RefereeContext);
  if (!context) {
    throw new Error('useRefereeAuth must be used within a RefereeProvider');
  }
  return context;
};